import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  ArrowLeftRight,
  Banknote,
  BarChart3,
  BookOpen,
  Building2,
  FileText,
  FolderKanban,
  HandCoins,
  Handshake,
  HardHat,
  House,
  Landmark,
  ListChecks,
  Package,
  Receipt,
  Search,
  SlidersHorizontal,
  Tags,
  Users,
  Wrench,
  XIcon,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandInputLeading,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { TOUR_ANCHOR } from "@/components/product-tour/tourSteps";
import { Button } from "@/components/ui/button";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import type { Permission } from "@/types";

type PaletteLinkDef = {
  permission: Permission;
  to: string;
  labelKey: string;
  Icon: LucideIcon;
};

type ProjectPaletteLinkDef = Omit<PaletteLinkDef, "to"> & {
  suffix: string;
};

const APP_PALETTE_NAV: PaletteLinkDef[] = [
  {
    permission: "nav.dashboard",
    to: "/dashboard",
    labelKey: "nav.dashboard",
    Icon: House,
  },
  {
    permission: "nav.dashboard",
    to: "/acompanhamento",
    labelKey: "nav.tracking",
    Icon: BarChart3,
  },
  {
    permission: "project.clientBudgets.view",
    to: "/client-budgets",
    labelKey: "nav.clientBudgets",
    Icon: FileText,
  },
  { permission: "nav.projects", to: "/projects", labelKey: "nav.projects", Icon: FolderKanban },
];

const PROJECT_PALETTE: ProjectPaletteLinkDef[] = [
  {
    permission: "project.projectDiary.view",
    suffix: "project-diary-entries",
    labelKey: "project.sections.projectDiaryEntries",
    Icon: BookOpen,
  },
  {
    permission: "project.projectReport.view",
    suffix: "reports",
    labelKey: "nav.pdf",
    Icon: FileText,
  },
  {
    permission: "project.finance.view",
    suffix: "costs",
    labelKey: "project.sections.costs",
    Icon: Landmark,
  },
  {
    permission: "finance.payables.view",
    suffix: "payables",
    labelKey: "project.sections.payables",
    Icon: Banknote,
  },
  {
    permission: "finance.receivables.view",
    suffix: "receivables",
    labelKey: "project.sections.receivables",
    Icon: HandCoins,
  },
  {
    permission: "finance.fiscal.view",
    suffix: "invoices",
    labelKey: "project.sections.invoices",
    Icon: Receipt,
  },
  {
    permission: "finance.cashFlow.view",
    suffix: "cash-flow",
    labelKey: "project.sections.cashFlow",
    Icon: ArrowLeftRight,
  },
];

const SETTINGS_PALETTE: PaletteLinkDef[] = [
  {
    permission: "settings.serviceFrontModels",
    to: "/settings/project-service-fronts-models",
    labelKey: "nav.serviceFrontModels",
    Icon: Users,
  },
  {
    permission: "settings.checklistModels",
    to: "/settings/checklist-models",
    labelKey: "nav.checklistModels",
    Icon: ListChecks,
  },
  {
    permission: "settings.occurrences",
    to: "/settings/occurrences",
    labelKey: "nav.occurrences",
    Icon: AlertTriangle,
  },
  {
    permission: "settings.lookups",
    to: "/settings/project-priorities",
    labelKey: "nav.classifications",
    Icon: Tags,
  },
  {
    permission: "settings.workers",
    to: "/settings/workers/roles",
    labelKey: "nav.workers",
    Icon: HardHat,
  },
  {
    permission: "nav.settings",
    to: "/settings/materials/measurement-units",
    labelKey: "nav.materials",
    Icon: Package,
  },
  {
    permission: "settings.equipments",
    to: "/settings/equipments",
    labelKey: "nav.equipments",
    Icon: Wrench,
  },
  {
    permission: "settings.partners",
    to: "/settings/partners",
    labelKey: "nav.partners",
    Icon: Handshake,
  },
  {
    permission: "settings.financeLookups",
    to: "/settings/financial-categories",
    labelKey: "nav.financeLookups",
    Icon: Landmark,
  },
  {
    permission: "settings.fiscal",
    to: "/settings/service-tax-codes",
    labelKey: "nav.serviceTaxCodes",
    Icon: Receipt,
  },
  {
    permission: "settings.organization",
    to: "/settings/general",
    labelKey: "settings.nav.general",
    Icon: SlidersHorizontal,
  },
  {
    permission: "settings.organization",
    to: "/settings/organization",
    labelKey: "settings.nav.organization",
    Icon: Building2,
  },
];

type GlobalSearchCtx = {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
};

const GlobalSearchContext = createContext<GlobalSearchCtx | null>(null);

function useGlobalSearch(): GlobalSearchCtx {
  const ctx = useContext(GlobalSearchContext);

  if (!ctx) {
    throw new Error("useGlobalSearch must be used within GlobalSearchProvider");
  }
  return ctx;
}

function GlobalSearchDialog(): ReactElement {
  const { t } = useTranslation();
  const { open, setOpen } = useGlobalSearch();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { can } = usePermissions();

  const appLinks = useMemo(() => APP_PALETTE_NAV.filter((item) => can(item.permission)), [can]);
  const projectLinks = useMemo(() => {
    if (!projectId) return [];
    return PROJECT_PALETTE.filter((item) => can(item.permission)).map((item) => ({
      ...item,
      to: `/projects/${projectId}/${item.suffix}`,
    }));
  }, [projectId, can]);
  const links = useMemo(() => [...appLinks, ...projectLinks], [appLinks, projectLinks]);
  const settingsLinks = useMemo(
    () => SETTINGS_PALETTE.filter((item) => can(item.permission)),
    [can],
  );

  const canSettings = settingsLinks.length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        aria-describedby={undefined}
        containInDrawer={false}
        className="overflow-hidden gap-3 p-0 shadow-lg outline-none ring-foreground/15 sm:max-w-lg"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{t("globalSearch.dialogTitle")}</DialogTitle>
        <DialogDescription className="sr-only">{t("globalSearch.dialogDesc")}</DialogDescription>
        <Command loop>
          <CommandInputLeading className="-mt-px pr-1.5">
            <CommandInput
              aria-label={t("globalSearch.ariaLabel")}
              placeholder={t("globalSearch.palettePlaceholder")}
              className="flex-1"
            />
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0"
                aria-label={t("common.closeFlow")}
              >
                <XIcon />
              </Button>
            </DialogClose>
          </CommandInputLeading>
          <CommandList className="max-h-[min(54vh,20rem)]">
            <CommandEmpty>{t("globalSearch.empty")}</CommandEmpty>
            {links.length > 0 ? (
              <CommandGroup heading={t("globalSearch.sectionGoTo")}>
                {links.map((item) => {
                  const Icon = item.Icon;
                  return (
                    <CommandItem
                      key={item.to}
                      value={`${item.to} ${t(item.labelKey)}`}
                      keywords={[t(item.labelKey)]}
                      onSelect={() => {
                        navigate(item.to);
                        setOpen(false);
                      }}
                    >
                      <Icon aria-hidden />
                      {t(item.labelKey)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
            {links.length > 0 && canSettings ? <CommandSeparator /> : null}
            {canSettings ? (
              <CommandGroup heading={t("globalSearch.sectionSettings")}>
                {settingsLinks.map((item) => {
                  const Icon = item.Icon;
                  return (
                    <CommandItem
                      key={item.to}
                      value={`${item.to} ${t(item.labelKey)}`}
                      keywords={[t(item.labelKey)]}
                      onSelect={() => {
                        navigate(item.to);
                        setOpen(false);
                      }}
                    >
                      <Icon aria-hidden />
                      {t(item.labelKey)}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function useSearchShortcutLabel(): string {
  const { t } = useTranslation();
  const [label, setLabel] = useState(() => t("globalSearch.shortcutWin"));

  useEffect(() => {
    const isApple =
      /Mac|iPhone|iPod|iPad/.test(navigator.platform) || /Mac OS X/.test(navigator.userAgent);
    setLabel(isApple ? t("globalSearch.shortcutMac") : t("globalSearch.shortcutWin"));
  }, [t]);

  return label;
}

/** Wide pill trigger for desktop top bar; opens palette (also Ctrl/Cmd+K). */
export function GlobalSearchTrigger({ className }: { className?: string }): ReactElement {
  const { t } = useTranslation();
  const { setOpen } = useGlobalSearch();
  const shortcutLabel = useSearchShortcutLabel();

  return (
    <button
      type="button"
      data-tour={TOUR_ANCHOR.globalSearch}
      className={cn(
        "flex h-10 w-full min-w-0 items-center gap-2.5 rounded-full border border-border bg-muted/50 px-3.5 text-sm text-muted-foreground shadow-none transition-colors outline-none hover:bg-muted/80 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
        className,
      )}
      aria-label={t("globalSearch.openAria")}
      onClick={() => setOpen(true)}
    >
      <Search className="size-4 shrink-0 opacity-70" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-left">
        {t("globalSearch.triggerPlaceholder")}
      </span>
      <kbd className="hidden shrink-0 rounded-md border border-border/80 bg-background/80 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
        {shortcutLabel}
      </kbd>
    </button>
  );
}

/** Compact control; opens palette (also Ctrl/Cmd+K). */
export function GlobalSearchIconButton({ className }: { className?: string }): ReactElement {
  const { t } = useTranslation();
  const { setOpen } = useGlobalSearch();

  return (
    <Button
      type="button"
      variant="outline"
      size="icon-sm"
      data-tour={TOUR_ANCHOR.globalSearch}
      className={cn("border-border size-9 shrink-0 shadow-none", className)}
      aria-label={t("globalSearch.openAria")}
      onClick={() => setOpen(true)}
    >
      <Search className="size-4" aria-hidden />
    </Button>
  );
}

export function GlobalSearchProvider({ children }: { children: ReactNode }): ReactElement {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  const value = useMemo(() => ({ open, setOpen, toggle }), [open, toggle]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || (e.key !== "k" && e.key !== "K")) return;

      const el = document.activeElement;
      if (!(el instanceof HTMLElement)) return;
      if (!el.closest("[data-global-search-force]")) {
        const blocked = el.closest<HTMLElement>(
          'input:not([readonly]), textarea:not([readonly]), select, [contenteditable="true"], [contenteditable="plaintext-only"]',
        );
        if (blocked) return;
      }

      e.preventDefault();
      setOpen((o) => !o);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      <GlobalSearchDialog />
    </GlobalSearchContext.Provider>
  );
}
